// Global Instructions Rule Applied!
// Frontend Instructions Rule Applied!
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Card, Typography, Alert } from 'antd'
import Button from '../../core/components/Button.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons'
import { performLogin } from '../../core/auth/authService'
import logo from '../../assets/logo.png'

const { Title, Text } = Typography

export default function LoginPage() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const dispatch = useDispatch()

  const handleLogin = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await performLogin({
        dispatch,
        onError: (errorMessage) => {
          setError(errorMessage)
        }
      })

      // If the login initiation fails, show the error and reset loading
      if (!result.success) {
        setLoading(false)
      }
      // On success we intentionally keep the loading state until the
      // OAuth flow redirects back, so the user sees the spinner.
    } catch (err) {
      setError('An unexpected error occurred during login')
      setLoading(false)
    }
  }

  return (
    <div
      className='relative min-h-dvh overflow-hidden flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'
      style={{ background: '#F0F7FF' }}
    >
      {/* Top Right Corner - Different Angled Overlapping Squares */}
      {/* Largest square - 30 degree rotation */}
      <div
        className='absolute top-0 right-0 w-[500px] h-[500px] transform rotate-[30deg] translate-x-40 -translate-y-40 opacity-70'
        style={{
          background: `linear-gradient(135deg, #FFFFFF 0%, #65A9DF 15%, #4C90E9 30%, #268ED2 45%, #6B8BE7 60%, #4A80AA 80%, #2887D0 100%)`,
          borderRadius: '32px'
        }}
      />
      {/* Medium square - 60 degree rotation */}
      <div
        className='absolute top-8 right-8 w-[400px] h-[400px] transform rotate-[60deg] translate-x-32 -translate-y-32 opacity-80'
        style={{
          background: `linear-gradient(135deg, #FFFFFF 0%, #268ED2 15%, #65A9DF 30%, #4C90E9 45%, #2887D0 60%, #6B8BE7 80%, #4A80AA 100%)`,
          borderRadius: '28px'
        }}
      />
      {/* Smallest square - 15 degree rotation */}
      <div
        className='absolute top-16 right-16 w-[300px] h-[300px] transform rotate-[15deg] translate-x-24 -translate-y-24 opacity-90'
        style={{
          background: `linear-gradient(135deg, #FFFFFF 0%, #4A80AA 15%, #6B8BE7 30%, #2887D0 45%, #268ED2 60%, #4C90E9 80%, #65A9DF 100%)`,
          borderRadius: '24px'
        }}
      />

      {/* Bottom Left Corner - Different Angled Overlapping Squares */}
      {/* Largest square - -45 degree rotation */}
      <div
        className='absolute bottom-0 left-0 w-[500px] h-[500px] transform rotate-[-45deg] -translate-x-40 translate-y-40 opacity-70'
        style={{
          background: `linear-gradient(225deg, #FFFFFF 0%, #2887D0 15%, #4A80AA 30%, #6B8BE7 45%, #268ED2 60%, #4C90E9 80%, #65A9DF 100%)`,
          borderRadius: '32px'
        }}
      />
      {/* Medium square - -15 degree rotation */}
      <div
        className='absolute bottom-8 left-8 w-[400px] h-[400px] transform rotate-[-15deg] -translate-x-32 translate-y-32 opacity-80'
        style={{
          background: `linear-gradient(225deg, #FFFFFF 0%, #65A9DF 15%, #268ED2 30%, #2887D0 45%, #4C90E9 60%, #4A80AA 80%, #6B8BE7 100%)`,
          borderRadius: '28px'
        }}
      />
      {/* Smallest square - -60 degree rotation */}
      <div
        className='absolute bottom-16 left-16 w-[300px] h-[300px] transform rotate-[-60deg] -translate-x-24 translate-y-24 opacity-90'
        style={{
          background: `linear-gradient(225deg, #FFFFFF 0%, #4C90E9 15%, #2887D0 30%, #65A9DF 45%, #6B8BE7 60%, #268ED2 80%, #4A80AA 100%)`,
          borderRadius: '24px'
        }}
      />

      {/* Main Content */}
      <div className='relative z-10 max-w-md w-full space-y-8'>
        {/* Login Card */}
        <Card
          className='shadow-lg border-0'
          style={{
            background: '#FFFFFF',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
            maxWidth: '400px',
            margin: '0 auto'
          }}
        >
          <div className='text-center space-y-6 py-8 px-6'>
            {/* Black Logo */}
            <div className='mx-auto w-20 h-20 rounded-full flex items-center justify-center'>
              <img src={logo} alt='Reservoir Logo' className='w-20 h-20' />
            </div>

            {/* Welcome Text */}
            <div className='space-y-2'>
              <Title level={2} className='!mb-0 !text-gray-900' style={{ fontSize: '2rem', fontWeight: '600' }}>
                Welcome to Reservoir
              </Title>
              <Text className='text-gray-600 text-lg'>We're excited to have you here.</Text>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert
                message={error}
                type='error'
                showIcon
                closable
                onClose={() => setError(null)}
                className='rounded-lg'
              />
            )}

            {/* Login Button */}
            <Button
              variant='primary'
              size='large'
              loading={loading}
              onClick={handleLogin}
              className='w-full h-12 text-base font-medium rounded-lg border-0'
              style={{
                background: '#4A90E2',
                fontSize: '16px'
              }}
            >
              {!loading && <FontAwesomeIcon icon={faMicrosoft} />}
              {loading ? 'Signing in...' : 'Sign in with Microsoft'}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}
